import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const BASE_URL = process.env.REACT_APP_BASE_URL || '';

// Display metadata for PGMA age bands. Order matters — drives row order in the grid.
// Labels are i18n keys resolved by the caller via t(band.labelKey). Do NOT
// bake language-specific strings here — dual-locale rendering was the reason
// French leaked into the English rate-editor view (T-18.4).
export const PGMA_AGE_BAND_ROWS = [
  { key: 'age_0_40',  labelKey: 'rateTables.pgmaAgeBand.age_0_40'  },
  { key: 'age_41_45', labelKey: 'rateTables.pgmaAgeBand.age_41_45' },
  { key: 'age_46_50', labelKey: 'rateTables.pgmaAgeBand.age_46_50' },
  { key: 'age_51_55', labelKey: 'rateTables.pgmaAgeBand.age_51_55' },
  { key: 'age_56_65', labelKey: 'rateTables.pgmaAgeBand.age_56_65' },
  { key: 'age_66_70', labelKey: 'rateTables.pgmaAgeBand.age_66_70' },
];

export const PGMA_WAITING_PERIODS = [30, 60, 90];

// State is stored in DISPLAY units (rates as percent, coveragePercent as integer
// 0–100). Conversion to/from decimals happens only at the API boundary.
// Round to 4 decimals so float artefacts like 0.00408 * 100 = 0.40800000000000003
// don't bleed into the input value. PDF source has at most 3 decimal places in
// percent units; 4 leaves headroom for future tariff updates.
const toDisplayPercent = (r) => Math.round(r * 100 * 10000) / 10000;

function ratesToDisplayGrid(rates) {
  const grid = {};
  for (const row of rates || []) {
    if (!grid[row.ageBand]) grid[row.ageBand] = {};
    grid[row.ageBand][row.waitingPeriod] = {
      maladieRate: toDisplayPercent(row.maladieRate),
      maladieAccidentRate: toDisplayPercent(row.maladieAccidentRate),
    };
  }
  return grid;
}

function constantsToDisplay(c = {}) {
  return {
    ...c,
    coveragePercent: c.coveragePercent != null ? Math.round(c.coveragePercent * 100) : 80,
  };
}

const useRateTablesFunctions = () => {
  const [table, setTable] = useState(null);
  const [grid, setGrid] = useState({});
  const [constants, setConstants] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [error, setError] = useState('');
  const [toast, setToast] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const resp = await axios.get(`${BASE_URL}/offers/pgma/rate-table`, { withCredentials: true });
      const data = resp.data?.data;
      setTable(data);
      setGrid(ratesToDisplayGrid(data?.rates));
      setConstants(constantsToDisplay(data?.constants));
      setDirty(false);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load rate table');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const updateCell = useCallback((bandKey, waitingPeriod, field, value) => {
    setGrid(prev => {
      const next = { ...prev, [bandKey]: { ...(prev[bandKey] || {}) } };
      next[bandKey][waitingPeriod] = { ...(next[bandKey][waitingPeriod] || {}), [field]: value };
      return next;
    });
    setDirty(true);
  }, []);

  const updateConstant = useCallback((field, value) => {
    setConstants(prev => ({ ...prev, [field]: value }));
    setDirty(true);
  }, []);

  const save = useCallback(async () => {
    setSaving(true);
    setError('');
    try {
      const rates = [];
      for (const band of PGMA_AGE_BAND_ROWS) {
        for (const wp of PGMA_WAITING_PERIODS) {
          const cell = grid?.[band.key]?.[wp];
          if (!cell) continue;
          rates.push({
            ageBand: band.key,
            waitingPeriod: wp,
            maladieRate: Number(cell.maladieRate) / 100,
            maladieAccidentRate: Number(cell.maladieAccidentRate) / 100,
          });
        }
      }
      const constantsToSend = {
        maxSalary: Number(constants.maxSalary),
        coveragePercent: Number(constants.coveragePercent) / 100,
        maxBenefitDays: Number(constants.maxBenefitDays),
        maxBenefitDaysAVS: Number(constants.maxBenefitDaysAVS),
        minimumPremium: Number(constants.minimumPremium),
        maxEligibleAge: Number(constants.maxEligibleAge),
        avsAgeMen: Number(constants.avsAgeMen),
        avsAgeWomen: Number(constants.avsAgeWomen),
      };
      const resp = await axios.put(
        `${BASE_URL}/offers/pgma/rate-table`,
        { rates, constants: constantsToSend },
        { withCredentials: true },
      );
      const data = resp.data?.data;
      setTable(data);
      setGrid(ratesToDisplayGrid(data?.rates));
      setConstants(constantsToDisplay(data?.constants));
      setDirty(false);
      setToast('Rate table saved');
      setTimeout(() => setToast(''), 2400);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save rate table');
    } finally {
      setSaving(false);
    }
  }, [grid, constants]);

  return {
    table,
    grid,
    constants,
    updateCell, updateConstant,
    loading, saving, dirty, error, toast,
    save,
    reload: load,
  };
};

export default useRateTablesFunctions;
